/**
 * Outbound webhooks: tenant-configured URLs the platform POSTs events
 * (message.delivered, message.failed, inbound.received) to. CRUD lives
 * here; the actual fan-out is the webhook-sender worker.
 *
 * The Create response includes the secret in plain text — that's the
 * ONLY time the API exposes it, so the page should show it once with
 * a "copy now, never shown again" warning.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export type WebhookEventType =
  | 'message.delivered'
  | 'message.failed'
  | 'inbound.received';

export interface WebhookEndpoint {
  id: number;
  uuid: string;
  url: string;
  events: WebhookEventType[];
  is_active: boolean;
  description?: string;
  /** Only present in the Create response. List/Get omit it. */
  secret?: string;
  created_at: string;
  updated_at: string;
}

export interface WebhookEndpointsResponse {
  items: WebhookEndpoint[];
}

export interface WebhookDelivery {
  id: number;
  uuid: string;
  endpoint_id: number;
  event_type: WebhookEventType | 'webhook.test';
  delivery_status: 'pending' | 'success' | 'failed';
  response_status?: number;
  error_message?: string;
  attempts: number;
  last_attempt_at?: string;
  /** When set, this row is "failed last time, will retry at this
   *  timestamp". When clear AND status='failed', it's terminal — gave
   *  up after maxAttempts. */
  next_retry_at?: string;
  created_at: string;
}

export interface WebhookDeliveriesResponse {
  items: WebhookDelivery[];
  total: number;
  limit: number;
  offset: number;
}

const WEBHOOKS_KEY = ['webhooks', 'list'] as const;
const DELIVERIES_KEY = (endpointId: number) => ['webhooks', 'deliveries', endpointId] as const;

export function useWebhooks() {
  return useQuery({
    queryKey: WEBHOOKS_KEY,
    queryFn: () => api.get<WebhookEndpointsResponse>('/v1/webhooks'),
    staleTime: 30_000,
  });
}

export interface CreateWebhookInput {
  url: string;
  events: WebhookEventType[];
  description?: string;
}

export function useCreateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWebhookInput) =>
      api.post<WebhookEndpoint>('/v1/webhooks', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: WEBHOOKS_KEY });
    },
  });
}

export interface UpdateWebhookInput {
  url?: string;
  events?: WebhookEventType[];
  is_active?: boolean;
  description?: string;
}

export function useUpdateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateWebhookInput }) =>
      api.patch<WebhookEndpoint>(`/v1/webhooks/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: WEBHOOKS_KEY });
    },
  });
}

export function useDeleteWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/v1/webhooks/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: WEBHOOKS_KEY });
    },
  });
}

export function useWebhookDeliveries(endpointId: number, opts?: { limit?: number; enabled?: boolean }) {
  const limit = opts?.limit ?? 50;
  return useQuery({
    queryKey: DELIVERIES_KEY(endpointId),
    queryFn: () =>
      api.get<WebhookDeliveriesResponse>(
        `/v1/webhooks/${endpointId}/deliveries?limit=${limit}`,
      ),
    enabled: opts?.enabled ?? true,
    staleTime: 10_000,
  });
}

/**
 * Fire a synthetic test event to one specific endpoint. The platform
 * records it in the delivery history like any other event so the
 * operator can verify URL + signature handling end-to-end.
 */
export function useTestWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (endpointId: number) =>
      api.post<void>(`/v1/webhooks/${endpointId}/test`),
    onSuccess: (_data, endpointId) => {
      // Invalidate the deliveries list so the test row shows up after
      // the worker processes it (queue → POST → DB write — usually <1s).
      void qc.invalidateQueries({ queryKey: DELIVERIES_KEY(endpointId) });
    },
  });
}

/**
 * Re-fire a past delivery to its endpoint. The original row stays as
 * audit; a NEW delivery row gets created (matches Stripe's pattern).
 * Used for manually retrying a give-up'd row or testing a known-good
 * endpoint with a real past event.
 */
export function useRetryDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ endpointId, deliveryId }: { endpointId: number; deliveryId: number }) =>
      api.post<void>(`/v1/webhooks/${endpointId}/deliveries/${deliveryId}/retry`),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: DELIVERIES_KEY(vars.endpointId) });
    },
  });
}
